import { Page, Text, View, Document, StyleSheet, PDFDownloadLink, Image } from "@react-pdf/renderer";
import { etiquetas, logos } from "../../../hook/Arreglos";

// Estilos del PDF
const styles = StyleSheet.create({
    // Página y encabezado
    page: { padding: 20, fontFamily: "Helvetica" },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 10,
    },
    logo: { width: 80, height: "auto" },
    contractNumber: { fontSize: 10, fontWeight: "bold", marginTop: 5 },

    // Tarjetas de información
    cardContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 10, // Espaciado entre tarjetas
    },
    card: {
        width: "32%", // Ancho ajustado para que los cuadros sean más anchos
        borderWidth: 1,
        borderColor: "#ccc",
        borderRadius: 5,
        padding: 8,
        backgroundColor: "#f9f9f9",
    },
    cardTitle: { fontSize: 10, fontWeight: "bold", color: "#000", marginBottom: 6 }, // Títulos en negro
    cardText: { fontSize: 7, color: "#333", marginBottom: 3 }, // Valores en gris oscuro
    divider: { borderBottomWidth: 1, borderBottomColor: "#ccc", marginVertical: 2 },

    // Tabla
    section: { marginBottom: 15 },
    title: {
        fontSize: 10, // Ajusta el tamaño del texto si es necesario
        fontWeight: "bold", // Asegúrate de que sea negrita
        textAlign: "center", // Centra el texto
        marginBottom: 8, // Margen inferior para separación
        backgroundColor: "#000", // Fondo negro
        color: "#fff", // Texto blanco
        padding: 4, // Agrega un poco de espacio interno
        textTransform: "uppercase", // Opcional: convierte el texto a mayúsculas
    },
    tableHeader: {
        backgroundColor: "#000",
        color: "#fff",
        padding: 3,
        fontSize: 8,
        fontWeight: "bold",
        textAlign: "center",
    },
    tableRow: { flexDirection: "row", borderBottomWidth: 1, borderColor: "#ccc", padding: 3 },
    tableCell: { fontSize: 7, padding: 2, textAlign: "left" }, // Justificar texto a la izquierda

    // Totales
    totalContainer: {
        marginTop: 10,
        alignItems: "flex-end", // Alinear todo a la derecha
        width: "100%", // Asegurar que el contenedor ocupe todo el ancho disponible
    },
    totalRow: {
        flexDirection: "row",
        justifyContent: "space-between", // Espaciado entre la etiqueta y el valor
        alignItems: "center", // Centrar verticalmente los elementos
        width: "30%", // Contenedor más compacto
        borderBottomWidth: 1, // Agregar una línea separadora
        borderBottomColor: "#ccc", // Color de la línea separadora
        paddingVertical: 2, // Espaciado interno para evitar que se vean amontonados
    },
    totalLabel: {
        fontSize: 8,
        fontWeight: "bold",
        textAlign: "left", // Alinear texto a la izquierda
        flex: 1, // Mantener proporción flexible
        marginRight: 2, // Espacio mínimo entre etiqueta y valor
    },
    totalValue: {
        fontSize: 8,
        textAlign: "right", // Alinear los valores numéricos a la derecha
        flex: 1, // Mantener proporción flexible
    },
});

// Función para asignar el logo correspondiente a un hotel
const asignarLogo = (idHotel) => {
    const hotel = logos.find((item) => item.id === Number(idHotel));
    // Return a default logo path if no match is found
    return hotel?.logo || "/assets/logo2.jpg";
};

// Función para asignar etiquetas a los hoteles
const asignarEtiqueta = (idHotel) => {
    const hotel = etiquetas.find((item) => item.id === idHotel);
    // Return null if no match is found to skip rendering
    return hotel?.logo || null;
};

// Componente principal del PDF
const PDFGenerator = ({ formulario, monedaActiva }) => (
    <Document>
        <Page size="A4" style={styles.page}>
            {/* Encabezado */}
            <View style={styles.header}>
                {asignarLogo(formulario.hotelSeleccionado) && (
                    <Image src={asignarLogo(formulario.hotelSeleccionado)} style={styles.logo} />
                )}
                <Text style={styles.contractNumber}>#{formulario.consecutivo}</Text>
            </View>

            {/* Tarjetas de información */}
            <View style={styles.cardContainer}>
                {/* Información del Ejecutivo */}
                <View style={styles.card}>
                    <Text style={styles.cardTitle}>Información del Ejecutivo</Text>
                    <Text style={styles.cardText}>Ejecutiva: {formulario.nombreEjecutivo}</Text>
                    <View style={styles.divider} />
                    <Text style={styles.cardText}>Cargo: {formulario.cargoEjecutivo}</Text>
                    <View style={styles.divider} />
                    <Text style={styles.cardText}>Correo Electrónico: {formulario.correoEjecutivo}</Text>
                    <View style={styles.divider} />
                    <Text style={styles.cardText}>Teléfono: {formulario.telefonoEjecutivo}</Text>
                </View>

                {/* Información del Cliente */}
                <View style={styles.card}>
                    <Text style={styles.cardTitle}>Información del Cliente</Text>
                    <Text style={styles.cardText}>Empresa: {formulario.empresaSeleccionada}</Text>
                    <View style={styles.divider} />
                    <Text style={styles.cardText}>Cliente: {formulario.nombreCliente}</Text>
                    <View style={styles.divider} />
                    <Text style={styles.cardText}>Correo Electrónico: {formulario.correoCliente}</Text>
                    <View style={styles.divider} />
                    <Text style={styles.cardText}>Teléfono: {formulario.telefonoCliente}</Text>
                </View>

                {/* Información del Contrato */}
                <View style={styles.card}>
                    <Text style={styles.cardTitle}>Información de contrato</Text>
                    <Text style={styles.cardText}>Número contrato: #{formulario.consecutivo}</Text>
                    <View style={styles.divider} />
                    <Text style={styles.cardText}>Fecha contrato: {formulario.fechaCreacion}</Text>
                    <View style={styles.divider} />
                    <Text style={styles.cardText}>Fechas Evento: {formulario.fechaSeleccionadas || "Sin definir"}</Text>
                    <View style={styles.divider} />
                    <Text style={styles.cardText}>Nombre Evento: {formulario.nombreEvento}</Text>
                </View>
            </View>

            {/* Tabla de requerimientos */}
            {formulario.requerimientos.length > 0 && (
                <View>
                    <Text style={styles.title}>REQUERIMIENTO DE EVENTO</Text>
                    <View>
                        <View style={[styles.tableRow, { backgroundColor: "#f9f9f9" }]}>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Fecha</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Hora</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Cant.</Text>
                            <Text style={[styles.tableCell, { width: "25%" }]}>Desc. artículo</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Días</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Salones</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Precio {monedaActiva}</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Subto. {monedaActiva}</Text>
                        </View>

                        {formulario.requerimientos.map((requerimiento, index) => (
                            <View key={index} style={styles.tableRow}>
                                <Text style={[styles.tableCell, { width: "10%" }]}>
                                    {requerimiento.fecha
                                        ? requerimiento.fecha
                                              .toString()
                                              .split(",")
                                              .map((part, idx) => (
                                                  <Text key={idx} style={{ display: "block" }}>
                                                      {part.trim()}
                                                  </Text>
                                              ))
                                        : "Sin definir"}
                                </Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.hora || "Sin definir"}</Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.cantidad}</Text>
                                <Text style={[styles.tableCell, { width: "25%" }]}>{requerimiento.descripcionArticulo}</Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.dias}</Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.salon || "Sin definir"}</Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>
                                    {requerimiento.precio} {monedaActiva}
                                </Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.subtotal}</Text>
                            </View>
                        ))}
                    </View>

                    <View style={styles.totalContainer}>
                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>SUB TOTAL {monedaActiva}:</Text>
                            <Text style={styles.totalValue}>{formulario.totalRequerimientos.subtReq_Subt}</Text>
                        </View>
                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>13% IM.VENTAS {monedaActiva}:</Text>
                            <Text style={styles.totalValue}>{formulario.totalRequerimientos.treceReq_Subt}</Text>
                        </View>
                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>10% IMP.SERV {monedaActiva}:</Text>
                            <Text style={styles.totalValue}>{formulario.totalRequerimientos.diezReq_Subt}</Text>
                        </View>
                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>TOTAL A&B {monedaActiva}:</Text>
                            <Text style={styles.totalValue}>{formulario.totalRequerimientos.totalReq_Subt}</Text>
                        </View>
                    </View>
                </View>
            )}
            <View style={{ height: 20 }} />

            {formulario.otroServicios.length > 0 && (
                <View>
                    <Text style={styles.title}>OTROS SERVICIOS</Text>
                    <View>
                        <View style={[styles.tableRow, { backgroundColor: "#f9f9f9" }]}>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Fecha</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Hora</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Cant.</Text>
                            <Text style={[styles.tableCell, { width: "25%" }]}>Desc. artículo</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Días</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Salones</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Precio {monedaActiva}</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Subto {monedaActiva}</Text>
                        </View>

                        {formulario.otroServicios.map((requerimiento, index) => (
                            <View key={index} style={styles.tableRow}>
                                <Text style={[styles.tableCell, { width: "10%" }]}>
                                    {requerimiento.fecha
                                        ? requerimiento.fecha
                                              .toString()
                                              .split(",")
                                              .map((part, idx) => (
                                                  <Text key={idx} style={{ display: "block" }}>
                                                      {part.trim()}
                                                  </Text>
                                              ))
                                        : "Sin definir"}
                                </Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.hora || "Sin definir"}</Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.cantidad}</Text>
                                <Text style={[styles.tableCell, { width: "25%" }]}>{requerimiento.descripcionArticulo}</Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.dias}</Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.salon || "Sin definir"}</Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.precio}</Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.subtotal}</Text>
                            </View>
                        ))}
                    </View>
                    <View style={styles.totalContainer}>
                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>SUB TOTAL {monedaActiva}:</Text>
                            <Text style={styles.totalValue}>{formulario.totalRequerimientos.subtOtro_Subt}</Text>
                        </View>
                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>13% IM.VENTAS {monedaActiva}:</Text>
                            <Text style={styles.totalValue}>{formulario.totalRequerimientos.treceOtro_Subt}</Text>
                        </View>

                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>TOTAL MONTAJE {monedaActiva}:</Text>
                            <Text style={styles.totalValue}>{formulario.totalRequerimientos.totalOtro_Subt}</Text>
                        </View>
                    </View>
                </View>
            )}
            <View style={{ height: 20 }} />

            {formulario.requerimientoshabitaciones.length > 0 && (
                <View>
                    <Text style={styles.title}>REQUERIMIENTOS DE HABITACIONES</Text>
                    <View>
                        <View style={[styles.tableRow, { backgroundColor: "#f9f9f9" }]}>
                            <Text style={[styles.tableCell, { width: "15%" }]}>Fecha In</Text>
                            <Text style={[styles.tableCell, { width: "15%" }]}>Fecha Out</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Noches</Text>
                            <Text style={[styles.tableCell, { width: "25%" }]}>Desc. Habitación</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Cant. Habitación</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Tarifa * Noche {monedaActiva}</Text>
                            <Text style={[styles.tableCell, { width: "10%" }]}>Sub Total {monedaActiva}</Text>
                        </View>

                        {formulario.requerimientoshabitaciones.map((requerimiento, index) => (
                            <View key={index} style={styles.tableRow}>
                                <Text style={[styles.tableCell, { width: "15%" }]}>{requerimiento.fechin_roomreq || "Sin definir"}</Text>
                                <Text style={[styles.tableCell, { width: "15%" }]}>{requerimiento.fechout_roomreq || "Sin definir"}</Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.noches_roomreq || "0"}</Text>
                                <Text style={[styles.tableCell, { width: "25%" }]}>{requerimiento.dec_roomreq || "Sin descripción"}</Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.cant_roomreq || "0"}</Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.tarifa_roomreq || "0.00"}</Text>
                                <Text style={[styles.tableCell, { width: "10%" }]}>{requerimiento.sub_roomreq || "0.00"}</Text>
                            </View>
                        ))}
                    </View>
                    <View style={styles.totalContainer}>
                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>SUB TOTAL {monedaActiva}:</Text>
                            <Text style={styles.totalValue}>{formulario.totalRequerimientos.subtsubtRoom_Subt}</Text>
                        </View>
                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>13% IM.VENTAS {monedaActiva}:</Text>
                            <Text style={styles.totalValue}>{formulario.totalRequerimientos.trecesubtRoom_Subt}</Text>
                        </View>
                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>TIP MUCAMA {monedaActiva}:</Text>
                            <Text style={styles.totalValue}>{formulario.totalRequerimientos.mucamasubtRoom_Subt}</Text>
                        </View>
                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>TOTAL HABITACIONES {monedaActiva}:</Text>
                            <Text style={styles.totalValue}>{formulario.totalRequerimientos.totalsubtRoom_Subt}</Text>
                        </View>
                    </View>
                </View>
            )}

            {/* Salto de línea */}
            <View style={{ height: 20 }} />
            <View>
                {/* Título */}
                <Text style={styles.title}> TOTAL GENERAL {monedaActiva}</Text>
                {/* Contenedor del total */}
                <View style={{ flexDirection: "row", justifyContent: "flex-end", marginTop: 5 }}>
                    <View
                        style={{
                            minWidth: 120,
                            borderWidth: 1,
                            borderColor: "#ccc",
                            padding: 5,
                            backgroundColor: "#f5f5f5",
                            borderRadius: 4,
                            alignItems: "center", // Alinea el texto centrado dentro del contenedor
                        }}
                    >
                        <Text style={{ fontSize: 10, fontWeight: "bold", textAlign: "right" }}>
                            {monedaActiva}
                             {formulario.totalGlobalCotizacion}
                        </Text>
                    </View>
                </View>
            </View>
            <View>
                {/* Título */}
                <View style={{ marginBottom: 10 }}>
                    <Text style={{ fontSize: 10, fontWeight: "bold", marginBottom: 4 }}>Notas: {formulario.notaCoti || ""}</Text>
                </View>
            </View>

            {/* Bottom image - Add conditional rendering */}
            {asignarEtiqueta(formulario.hotelSeleccionado) && (
                <View style={{ alignItems: "center", marginVertical: 10 }}>
                    <Image
                        src={asignarEtiqueta(formulario.hotelSeleccionado)}
                        style={{
                            width: "100%",
                            height: "auto",
                        }}
                    />
                </View>
            )}
        </Page>
    </Document>
);

// Componente funcional para generar y descargar un PDF basado en los datos proporcionados.
// Recibe dos props: `formulario` (objeto con los datos del formulario) y `nombre` (nombre del archivo PDF).
const PDFDownload = ({ formulario, nombre }) => {
    // Obtener la moneda activa del formulario. Si no hay una moneda activa, se asigna "$" como valor predeterminado.
    const monedaActiva = formulario.MonedaTipo.find((moneda) => moneda.estado)?.nombre || "$";

    return (
        // Componente `PDFDownloadLink` de `@react-pdf/renderer` para generar y descargar el PDF.
        <PDFDownloadLink
            // Documento PDF que se generará. Se pasa el componente `PDFGenerator` con los datos necesarios.
            document={<PDFGenerator formulario={formulario} monedaActiva={monedaActiva} />}
            // Nombre del archivo PDF que se descargará. Se construye dinámicamente usando el nombre y el consecutivo del formulario.
            fileName={`${nombre}_${formulario.consecutivo}.pdf`}
        >
            {/* Renderizado condicional del botón de descarga. */}
            {({ loading }) => (
                <button type="button" className="btn btn-success me-2">
                    {loading ? (
                        // Muestra un spinner y un mensaje mientras se genera el PDF.
                        <>
                            <i className="ti ti-spinner ti-spin"></i> Generando PDF...
                        </>
                    ) : (
                        // Muestra el ícono de descarga y el nombre del archivo cuando el PDF está listo.
                        <>
                            <i className="ti ti-download"></i> Descargar {nombre}
                        </>
                    )}
                </button>
            )}
        </PDFDownloadLink>
    );
};

// Exporta el componente para su uso en otras partes de la aplicación.
export default PDFDownload;